import React, {
  useState,
  useRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  CalendarIcon,
  ClockIcon,
  CalendarClockIcon,
  MonthYearIcon,
  IconProps,
} from '../shared/Icons';
import {
  DateTimeVariant,
  DISPLAY_PLACEHOLDER,
  formatDisplay,
  formatWire,
  parseValue,
} from './datetime/dateUtils';
import {
  PickerPalette,
  buildPickerTheme,
  readPickerPalette,
} from './datetime/usePickerTheme';

const FIELD_ICON_SIZE = 18;

export type { DateTimeVariant };

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .datetime-input-wrap {
    position: relative;
    display: block;
    width: 100%;
  }


  .datetime-input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: #9ca3af;
    cursor: pointer;
    pointer-events: auto;
    z-index: 2;
    transition: color 0.15s ease;
  }

  .datetime-input-icon:hover {
    color: #4b5563;
  }

  .datetime-input {
    width: 100%;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 8.5px 14px;
    font-size: 1rem;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;
    background: transparent;
    cursor: pointer;
    transition: border-color 0.15s ease;
  }

  .datetime-input.with-icon {
    padding-left: 38px;
  }

  .datetime-input:focus {
    border-color: #0069ff;
  }

  .datetime-input:disabled {
    cursor: not-allowed;
  }

  &.has-error .datetime-input {
    border-color: #d32f2f;
  }

  .datetime-input-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin-top: 3px;
  }
`;

const RenderNothing = () => null;

const MINUTE_STEP_PROPS = {
  minutesStep: 1,
  timeSteps: { hours: 1, minutes: 1 },
} as const;

const VARIANT_ICON: Record<
  DateTimeVariant,
  React.FC<IconProps>
> = {
  date: CalendarIcon,
  time: ClockIcon,
  datetime: CalendarClockIcon,
  month: MonthYearIcon,
};

interface DateTimeInputData {
  field_name?: string;
  required?: boolean;
  value?: string;
  showIcon?: boolean;
  min?: string;
  max?: string;
}

interface DateTimeInputProps {
  data: DateTimeInputData;
  variant: DateTimeVariant;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;

  mutable?: boolean;
}

const DateTimeInput = React.forwardRef<
  HTMLInputElement,
  DateTimeInputProps
>((props, ref) => {
  const {
    field_name,
    required,
    value: initialValue,
    showIcon = true,
    min,
    max,
  } = props.data || {};
  const variant = props.variant || 'date';

  const [value, setValue] = useState<string>(
    initialValue || '',
  );
  const [touched, setTouched] = useState(false);
  const [open, setOpen] = useState(false);
  const [palette, setPalette] =
    useState<PickerPalette | null>(null);

  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => hiddenInputRef.current as HTMLInputElement,
  );

  const showError =
    props.mutable !== false &&
    touched &&
    required &&
    !value;
  const Icon = VARIANT_ICON[variant];

  const selectedDate = useMemo(
    () => parseValue(value, variant),
    [value, variant],
  );
  const minDate = useMemo(
    () => parseValue(min, variant),
    [min, variant],
  );
  const maxDate = useMemo(
    () => parseValue(max, variant),
    [max, variant],
  );
  const displayValue = useMemo(
    () => formatDisplay(value, variant),
    [value, variant],
  );

  const pickerTheme = useMemo(
    () =>
      buildPickerTheme(palette || readPickerPalette(null)),
    [palette],
  );

  const emitChange = (next: string) => {
    setValue(next);
    if (props.onChange && field_name) {
      props.onChange({ [field_name]: next });
    }
  };

  const handlePicked = (next: Date | null) => {
    if (!next || Number.isNaN(next.getTime())) {
      emitChange('');
      return;
    }
    emitChange(formatWire(next, variant));
  };
  const openPicker = () => {
    if (props.read_only || open) return;
    setPalette(
      (prev) =>
        prev ??
        readPickerPalette(
          anchorRef.current?.querySelector<HTMLElement>(
            '.datetime-input',
          ) || null,
        ),
    );
    setOpen(true);
  };

  const closePicker = () => {
    setOpen(false);
    setTouched(true);
  };
  const field = (
    <div
      className="datetime-input-wrap ahd-icon-input-wrap"
      ref={anchorRef}
    >
      {showIcon && (
        <span
          className="datetime-input-icon ahd-icon-input-icon"
          onClick={openPicker}
          role="presentation"
        >
          <Icon size={FIELD_ICON_SIZE} />
        </span>
      )}
      <input
        className={`datetime-input${showIcon ? ' with-icon' : ''}`}
        type="text"
        readOnly
        placeholder={DISPLAY_PLACEHOLDER[variant]}
        value={displayValue}
        disabled={props.read_only}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ArrowDown'
          ) {
            e.preventDefault();
            openPicker();
          }
        }}
      />
    </div>
  );

  const sharedPickerProps = {
    open,
    value: selectedDate,
    onChange: handlePicked,
    onClose: closePicker,
    disabled: props.read_only,
    minDate: minDate || undefined,
    maxDate: maxDate || undefined,
    slots: { field: RenderNothing },
    slotProps: {
      popper: {
        anchorEl: () => anchorRef.current as HTMLElement,
      },
    },
  } as const;

  return (
    <StyledWrapper className={showError ? 'has-error' : ''}>
      {field}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={pickerTheme}>
          {variant === 'time' ? (
            <TimePicker {...sharedPickerProps} {...MINUTE_STEP_PROPS} />
          ) : variant === 'datetime' ? (
            <DateTimePicker {...sharedPickerProps} {...MINUTE_STEP_PROPS} />
          ) : variant === 'month' ? (
            <DatePicker
              {...sharedPickerProps}
              views={['year', 'month']}
            />
          ) : (
            <DatePicker {...sharedPickerProps} />
          )}
        </ThemeProvider>
      </LocalizationProvider>
      {showError && (
        <div className="datetime-input-error-text">
          This field is required.
        </div>
      )}
      <input
        ref={hiddenInputRef}
        type="hidden"
        name={field_name}
        value={value}
      />
    </StyledWrapper>
  );
});

DateTimeInput.displayName = 'DateTimeInput';

const withVariant = (
  variant: DateTimeVariant,
  displayName: string,
) => {
  const Wrapped = React.forwardRef<
    HTMLInputElement,
    Omit<DateTimeInputProps, 'variant'>
  >((props, ref) => (
    <DateTimeInput {...props} variant={variant} ref={ref} />
  ));
  Wrapped.displayName = displayName;
  return Wrapped;
};

export const DateInput = withVariant('date', 'DateInput');
export const TimeInput = withVariant('time', 'TimeInput');
export const DateTimeLocalInput = withVariant(
  'datetime',
  'DateTimeLocalInput',
);
export const MonthYearInput = withVariant(
  'month',
  'MonthYearInput',
);

export default DateTimeInput;
